import { ChangeDetectionStrategy, Component, DestroyRef, Input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButton } from '@angular/material/button';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { Observable, Subject } from 'rxjs';

import { Pagination, PaginationMetadata } from '@core/shared/domain';
import {
  PlattformPackungseinheit,
  PlattformPackungseinheitMapping,
} from '@mp/content-manager/industriekataloge/data-access';

import { IndustriekatalogeFacade } from '../industriekataloge.facade';

import {
  PackungsUpdateEinheit,
  PackungseinheitenDialogContentComponent,
} from './packungseinheiten-dialog-content.component';

@Component({
  selector: 'mp-packungseinheiten-dialog',
  templateUrl: './packungseinheiten-dialog.component.html',
  styleUrl: './packungseinheiten-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButton, MatIcon],
})
export class PackungseinheitenDialogComponent {
  @Input() einheiten$ = new Observable<Array<PlattformPackungseinheitMapping>>();
  @Input() einheitenPagination$ = new Observable<PaginationMetadata>();
  @Input() datasources: Array<string> = [];
  @Input() optionen: Array<PlattformPackungseinheit> = [];

  private datasource$ = new Subject<string | undefined>();
  currentDatasource = '';

  private dialogRef?: MatDialogRef<PackungseinheitenDialogContentComponent>;

  constructor(
    public dialog: MatDialog,
    private industryFacade: IndustriekatalogeFacade,
    private readonly destroyRef: DestroyRef,
  ) {
    this.datasource$.pipe(takeUntilDestroyed()).subscribe((data) => {
      if (data) {
        this.currentDatasource = data;
        const pagination: Pagination = { page: 1, pageSize: 10 };

        this.industryFacade.getAllPlattformPackungseinheitenMappingsFromDataSource(data, pagination);
      } else {
        this.industryFacade.clearPlattformPackungseinheitenMappings();
      }
    });

    this.industryFacade.getPlattformPackungseinheiten();
    this.industryFacade.getAllDatasources();
  }

  openDialog(): void {
    this.dialogRef = this.dialog.open(PackungseinheitenDialogContentComponent, {
      minWidth: '80%',
      panelClass: 'mp-packungseinheiten-dialog-content',
      data: {
        einheiten$: this.einheiten$,
        einheitenPagination$: this.einheitenPagination$,
        optionen: this.optionen,
        datasources: this.datasources,
      },
    });

    this.dialogRef.componentInstance.emitDatasourceChanged.subscribe((value) => {
      this.datasource$.next(value);
    });

    this.dialogRef.componentInstance.pageEvent.subscribe((pagination) => {
      this.industryFacade.getAllPlattformPackungseinheitenMappingsFromDataSource(this.currentDatasource, pagination);
    });

    this.dialogRef.componentInstance.einheitenMappingSaved.subscribe(
      ({ einheit, mappedShortName }: PackungsUpdateEinheit) => {
        const updatePackungseinheitenDto = {
          articleType: einheit.articleType,
          dataSource: einheit.dataSource,
          shortName: einheit.shortName,
          mappedLongName: einheit.mappedLongName,
          mappedShortName: mappedShortName,
        } as PlattformPackungseinheitMapping;

        this.industryFacade.updateDatasourcePackungseinheit(updatePackungseinheitenDto);
      },
    );

    this.dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.industryFacade.clearPlattformPackungseinheitenMappings();
      });
  }
}
